import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { ConcentricRings } from '@/components/Motifs';
import { usePageTitle } from '@/hooks/usePageTitle';
import { ArrowLeft, ArrowRight, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  ARCHETYPES, GIFTS, SHADOWS, INVITATIONS, PONDER,
  ALLIANCES, ALLIANCE_WHY, FRICTION_WHY, TIER1, TIER2
} from '@/data/quizData';

const fadeVariant = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, x: -30, transition: { duration: 0.25 } }
};

export default function SacredFeminineQuizPage() {
  usePageTitle('Sacred Feminine Archetype Quiz');
  const [screen, setScreen] = useState('intro'); // intro, quiz, gate, email, result
  const [scores, setScores] = useState({ C: 0, W: 0, P: 0, S: 0, D: 0 });
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOpt, setSelectedOpt] = useState(null);
  const [allQuestions, setAllQuestions] = useState(TIER1);
  const [doDeeper, setDoDeeper] = useState(false);
  const [inTier2, setInTier2] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultData, setResultData] = useState(null);

  const getPrimary = (s) => Object.entries(s).sort((a, b) => b[1] - a[1])[0][0];
  const getSecondary = (s) => Object.entries(s).sort((a, b) => b[1] - a[1])[1][0];

  const startQuiz = () => {
    setScores({ C: 0, W: 0, P: 0, S: 0, D: 0 });
    setCurrentQ(0);
    setAnswers([]);
    setSelectedOpt(null);
    setAllQuestions(TIER1);
    setDoDeeper(false);
    setInTier2(false);
    setScreen('quiz');
  };

  const selectOption = (i) => {
    setSelectedOpt(i);
    const newAnswers = [...answers];
    newAnswers[currentQ] = i;
    setAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (selectedOpt === null && answers[currentQ] === undefined) return;
    const optIdx = selectedOpt !== null ? selectedOpt : answers[currentQ];
    const opt = allQuestions[currentQ].opts[optIdx];

    // Apply scores
    const newScores = { ...scores };
    Object.entries(opt.score).forEach(([k, v]) => { newScores[k] = (newScores[k] || 0) + v; });
    setScores(newScores);

    const nextIdx = currentQ + 1;
    if (nextIdx === 10 && !doDeeper) {
      setCurrentQ(nextIdx);
      setScreen('gate');
    } else if (nextIdx >= allQuestions.length) {
      setCurrentQ(nextIdx);
      setScreen('email');
    } else {
      setCurrentQ(nextIdx);
      setSelectedOpt(answers[nextIdx] !== undefined ? answers[nextIdx] : null);
    }
  };

  const goBack = () => {
    if (currentQ <= 0) return;
    const prev = currentQ - 1;
    if (prev < 10 && inTier2) setInTier2(false);
    setCurrentQ(prev);
    setSelectedOpt(answers[prev] !== undefined ? answers[prev] : null);
    // Reverse the score from current question if it was answered
    // (simplified: we don't reverse, user re-selects)
  };

  const goDeeper = () => {
    setDoDeeper(true);
    setAllQuestions([...TIER1, ...TIER2]);
    setInTier2(true);
    setSelectedOpt(null);
    setScreen('quiz');
  };

  const skipToResults = () => {
    setScreen('email');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    const primary = getPrimary(scores);
    const secondary = getSecondary(scores);
    try {
      await api.post('/quiz/submit', {
        email, name,
        scores,
        primary_archetype: primary,
        secondary_archetype: secondary,
        did_deeper: doDeeper,
        answers
      });
    } catch (err) {
      // Quiz submission is non-blocking — results still display locally
      // Email delivery handled server-side regardless
    }
    setResultData({ primary, secondary });
    setSubmitting(false);
    setScreen('result');
  };

  const totalQ = allQuestions.length;
  const progress = screen === 'quiz' ? ((currentQ) / totalQ) * 100 : 0;
  const question = allQuestions[currentQ];
  const primary = resultData?.primary;
  const secondary = resultData?.secondary;

  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* INTRO */}
      {screen === 'intro' && (
        <section className="hero-gradient relative min-h-[80vh] flex items-center overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06] bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1658237775407-aed6450f5851?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85')` }} />
          <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <p className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.22em] text-[hsl(var(--silver))] mb-6">
                The Cosmic Synthesist
              </p>
              <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-[56px] font-bold leading-[1.1] text-[hsl(var(--foreground))] mb-6">
                Sacred Feminine<br />Archetype Quiz
              </h1>
              <p className="font-['Inter'] text-base sm:text-lg leading-[1.8] text-[hsl(var(--muted-foreground))] mb-4 max-w-lg mx-auto">
                Discover which of the five Sacred Feminine Thinking Archetypes shapes how you lead, create, and make sense of the world.
              </p>
              <p className="font-['Inter'] text-sm text-[hsl(var(--silver))] mb-10">
                10 core questions &middot; Optional deeper tier &middot; 5 minutes
              </p>
              <Button
                onClick={startQuiz}
                className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-10 py-6 rounded-lg hover:brightness-[1.03]"
                data-testid="quiz-start-button"
              >
                Begin the Quiz
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      {/* QUIZ */}
      {screen === 'quiz' && question && (
        <section className="py-12 sm:py-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            {/* Progress */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-3">
                <span className="font-['Inter'] text-xs text-[hsl(var(--silver))]">
                  Question {currentQ + 1} of {totalQ}
                </span>
                <span className={`font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] px-2.5 py-0.5 rounded-full border ${
                  inTier2 && currentQ >= 10 ? 'border-[rgba(155,142,196,0.3)] text-[#9B8EC4]' : 'border-[rgba(201,168,76,0.25)] text-[hsl(var(--gold))]'
                }`}>
                  {inTier2 && currentQ >= 10 ? 'Deeper' : 'Core'}
                </span>
              </div>
              <div className="h-1 rounded-full bg-[rgba(168,181,178,0.1)]">
                <div className="h-full rounded-full bg-[hsl(var(--gold))] transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={currentQ} variants={fadeVariant} initial="initial" animate="animate" exit="exit">
                {/* Question type badge */}
                <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.18em] text-[hsl(var(--silver))] mb-4">
                  {question.type}
                </p>
                <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[hsl(var(--foreground))] mb-8 leading-[1.2]">
                  {question.q}
                </h2>

                {/* Options */}
                <div className="space-y-3">
                  {question.opts.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => selectOption(i)}
                      className={`w-full text-left p-4 sm:p-5 rounded-xl border transition-all duration-300 font-['Inter'] text-sm leading-[1.7] ${
                        (selectedOpt === i || answers[currentQ] === i)
                          ? 'border-[rgba(201,168,76,0.5)] bg-[rgba(27,77,68,0.2)] text-[hsl(var(--foreground))]'
                          : 'border-[rgba(168,181,178,0.1)] bg-[hsl(var(--card))] text-[hsl(var(--muted-foreground))] hover:border-[rgba(201,168,76,0.25)] hover:bg-[rgba(27,77,68,0.08)]'
                      }`}
                    >
                      {opt.t}
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-10">
              <Button
                variant="ghost"
                onClick={goBack}
                disabled={currentQ === 0}
                className="text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] disabled:opacity-30"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button
                onClick={nextQuestion}
                disabled={selectedOpt === null && answers[currentQ] === undefined}
                className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm px-8 py-5 rounded-lg disabled:opacity-30"
                data-testid="quiz-next-button"
              >
                {currentQ === allQuestions.length - 1 ? 'See Results' : 'Next'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* GATE (between tier 1 and 2) */}
      {screen === 'gate' && (
        <section className="py-16 sm:py-24">
          <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.18em] text-[hsl(var(--silver))] mb-6">
                Core complete
              </p>
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: ARCHETYPES[getPrimary(scores)].color + '33', border: `1px solid ${ARCHETYPES[getPrimary(scores)].color}55` }}>
                  <span className="font-['Cormorant_Garamond'] text-2xl font-bold" style={{ color: ARCHETYPES[getPrimary(scores)].color }}>
                    {getPrimary(scores)}
                  </span>
                </div>
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-3">
                Your primary signal: {ARCHETYPES[getPrimary(scores)].name}
              </h2>
              <p className="font-['Inter'] text-base text-[hsl(var(--muted-foreground))] mb-10">
                Want to explore your shadows, relationships, and how this archetype shows up in business? 10 more questions will sharpen the picture.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button onClick={goDeeper} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm px-8 py-5 rounded-lg">
                  Go Deeper
                </Button>
                <Button onClick={skipToResults} variant="outline" className="border-[rgba(201,168,76,0.28)] text-[hsl(var(--foreground))] font-['Inter'] text-sm px-8 py-5 rounded-lg">
                  See My Results
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* EMAIL GATE */}
      {screen === 'email' && (
        <section className="py-16 sm:py-24">
          <div className="max-w-md mx-auto px-4 sm:px-6 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <ConcentricRings size={60} opacity={0.15} className="mx-auto mb-6" />
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-4">
                Your results are ready.
              </h2>
              <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] mb-8">
                Enter your email to unlock your full archetype profile. We'll also send a copy to your inbox.
              </p>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <Input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
                />
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
                  data-testid="quiz-email-input"
                  required
                />
                <Button
                  type="submit"
                  disabled={submitting || !email}
                  className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium py-5 rounded-lg disabled:opacity-50"
                  data-testid="quiz-email-submit"
                >
                  {submitting ? 'Revealing...' : 'Reveal My Archetype'}
                </Button>
              </form>
              <p className="font-['Inter'] text-[11px] text-[hsl(var(--muted-foreground))] mt-4">
                You'll also receive occasional transmissions from The Cosmic Synthesist. Unsubscribe anytime.
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* RESULTS */}
      {screen === 'result' && primary && (
        <section className="py-12 sm:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              {/* Primary archetype */}
              <div className="text-center mb-12">
                <p className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.2em] text-[hsl(var(--silver))] mb-4">
                  Your Primary Archetype
                </p>
                <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold mb-2" style={{ color: ARCHETYPES[primary].color }}>
                  {ARCHETYPES[primary].name}
                </h1>
                <p className="font-['Cormorant_Garamond'] text-xl italic text-[hsl(var(--champagne))] mb-2">
                  {ARCHETYPES[primary].tagline}
                </p>
                <p className="font-['Inter'] text-xs text-[hsl(var(--silver))]">
                  {ARCHETYPES[primary].element}
                </p>
              </div>

              {/* Score bars */}
              <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6 sm:p-8 mb-10">
                <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4">Your Spectrum</p>
                <div className="space-y-3">
                  {Object.entries(scores).sort((a, b) => b[1] - a[1]).map(([key, val]) => {
                    const maxScore = Math.max(...Object.values(scores), 1);
                    return (
                      <div key={key} className="flex items-center gap-3">
                        <span className="font-['Inter'] text-xs w-24 text-[hsl(var(--muted-foreground))] shrink-0">{ARCHETYPES[key].name.replace('The ', '')}</span>
                        <div className="flex-1 h-2 rounded-full bg-[rgba(168,181,178,0.1)]">
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(val / maxScore) * 100}%`, background: ARCHETYPES[key].color }} />
                        </div>
                        <span className="font-['Inter'] text-xs text-[hsl(var(--silver))] w-6 text-right">{val}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Gift */}
              <div className="mb-10">
                <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-4">Your Core Gift</p>
                <p className="font-['Inter'] text-[15px] leading-[1.8] text-[hsl(var(--muted-foreground))]">{GIFTS[primary]}</p>
              </div>

              {/* Shadow */}
              <div className="mb-10">
                <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--rose))] mb-4">Your Shadow</p>
                <p className="font-['Inter'] text-[15px] leading-[1.8] text-[hsl(var(--muted-foreground))]">{SHADOWS[primary]}</p>
              </div>

              {/* Secondary */}
              <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6 sm:p-8 mb-10">
                <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-2">Your Full Spectrum</p>
                <p className="font-['Cormorant_Garamond'] text-xl font-semibold italic text-[hsl(var(--foreground))] mb-3">
                  {ARCHETYPES[primary].name} with {ARCHETYPES[secondary].name} undertones
                </p>
                <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))]">
                  Your secondary archetype of {ARCHETYPES[secondary].name} ({ARCHETYPES[secondary].tagline}) adds depth to how your primary expresses itself. This combination shapes how you integrate, create, and lead.
                </p>
              </div>

              {/* Relationships */}
              {(() => {
                const alliance = ALLIANCES[primary];
                const bestKey = primary + alliance.best;
                const frictionKey = primary + alliance.friction;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                    <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(122,158,126,0.25)] p-6">
                      <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--sage))] mb-3">Generative Alliance</p>
                      <p className="font-['Cormorant_Garamond'] text-lg font-semibold text-[hsl(var(--foreground))] mb-2">{ARCHETYPES[alliance.best].name}</p>
                      <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))]">{ALLIANCE_WHY[bestKey] || ''}</p>
                    </div>
                    <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(185,105,122,0.2)] p-6">
                      <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--rose))] mb-3">Growth Friction</p>
                      <p className="font-['Cormorant_Garamond'] text-lg font-semibold text-[hsl(var(--foreground))] mb-2">{ARCHETYPES[alliance.friction].name}</p>
                      <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))]">{FRICTION_WHY[frictionKey] || ''}</p>
                    </div>
                  </div>
                );
              })()}

              {/* Invitation */}
              <div className="mb-10">
                <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-4">Your Invitation</p>
                <p className="font-['Cormorant_Garamond'] text-lg sm:text-xl italic leading-[1.5] text-[hsl(var(--foreground))]">{INVITATIONS[primary]}</p>
              </div>

              {/* Ponder */}
              <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6 sm:p-8 mb-12">
                <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4">Ponder This</p>
                <p className="font-['Cormorant_Garamond'] text-lg italic leading-[1.5] text-[hsl(var(--champagne))]">{PONDER[primary]}</p>
              </div>

              {/* CTAs */}
              <div className="text-center space-y-4">
                <Link to="/sacred-feminine-archetypes" className="inline-flex items-center gap-2 font-['Inter'] text-sm text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors">
                  Explore the full framework <ChevronRight className="h-4 w-4" />
                </Link>
                <div>
                  <button onClick={() => { setScreen('intro'); setResultData(null); setEmail(''); setName(''); }} className="font-['Inter'] text-xs text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] transition-colors">
                    Retake the quiz
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      <SiteFooter />
    </div>
  );
}
